import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Gallery } from '../../types/Gallery';
import { Category, loadCategories } from '../../utils/categories';
import { loadSampleGalleries } from '../../utils/sampleData';
import { dataSync } from '../../utils/dataSync';
import { getGalleryById, loadLiveGalleries } from '../../utils/GalleryAPI';
import { applyHeroImagePositioning, getCSSObjectPosition } from '../../utils/ImagePositioning';
import { cloudinaryDeliveryUrl, cloudinaryLightbox, cloudinaryLightboxWidth, cloudinarySrcSet, photographAlt } from '../../utils/imageSeo';
import sitePaths from '../../utils/sitePaths';
import GalleryInfoDialog from './GalleryInfoDialog';
import { findCategory } from '../../utils/galleryInfoCopy';
import ShareButton from '../Sharing/ShareButton';
import ShareModal from '../Sharing/ShareModal';
import { generateShareContent, ShareableContent } from '../../utils/sharingUrls';
import { fitShareImageUrl, getConfiguredShareImage, getPublicSiteUrl, normalizePublicImageUrl } from '../../utils/publicSiteUrl';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import BrandMarkSpinner from '../Loading/BrandMarkSpinner';
import NewMasonryGallery from './NewMasonryGallery';
import CarouselGallery from './CarouselGallery';
import MagazineGallery from './MagazineGallery';
import PortfolioGallery from './PortfolioGallery';
import CinematicGallery from './CinematicGallery';
import SingleGallery from './SingleGallery';
import RotatingGallery from './RotatingGallery';
import { useMobileViewer, usePhotographStage } from './usePhotographStage';
import './OriginalGalleryView.css';

const MODAL_PREVIEW_WIDTH = 240;

const galleryImageUrl = (image?: { url?: string; secure_url?: string } | null) =>
  image?.url || image?.secure_url || '';

const preloadImage = (url: string) => {
  if (!url) return;
  const loader = new Image();
  loader.decoding = 'async';
  loader.src = url;
};

interface OriginalGalleryViewProps {
  galleries?: Gallery[];
  onGalleryHeadingChange?: (heading: string) => void;
}

// Height-balanced masonry. Column choice uses each photo's height/width ratio,
// then updates from the loaded image. It does not use list position as a height.
interface OptimizedMasonryProps {
  breakpointCols: { default: number; [key: number]: number };
  className: string;
  children: React.ReactNode;
  recalculationKey?: string;
}

const MASONRY_GAP = 14;

const OptimizedMasonry: React.FC<OptimizedMasonryProps> = ({ breakpointCols, className, children, recalculationKey }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [numColumns, setNumColumns] = useState(breakpointCols.default);
  const [distributedItems, setDistributedItems] = useState<React.ReactNode[][]>([]);
  const aspectRef = useRef<Map<string, number>>(new Map());
  const [aspectVersion, setAspectVersion] = useState(0);

  useEffect(() => {
    let resizeTimer: number | undefined;
    let lastWidth = -1;

    const applyColumns = (currentWidth: number) => {
      if (Math.abs(currentWidth - lastWidth) < 8) return;
      lastWidth = currentWidth;
      let cols = breakpointCols.default;
      const breakpoints = Object.keys(breakpointCols)
        .filter(key => key !== 'default')
        .map(Number)
        .sort((a, b) => b - a);
      for (const bp of breakpoints) {
        if (currentWidth <= bp) cols = breakpointCols[bp];
      }
      setNumColumns(cols);
    };

    const updateColumns = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        applyColumns(document.documentElement.clientWidth || window.innerWidth);
      }, 120);
    };

    applyColumns(document.documentElement.clientWidth || window.innerWidth);
    window.addEventListener('resize', updateColumns);
    window.addEventListener('orientationchange', updateColumns);
    return () => {
      window.removeEventListener('resize', updateColumns);
      window.removeEventListener('orientationchange', updateColumns);
      window.clearTimeout(resizeTimer);
    };
  }, [breakpointCols]);

  useEffect(() => {
    let timer: number | undefined;
    const onAspect = (event: Event) => {
      const detail = (event as CustomEvent<{ id?: string; aspect?: number }>).detail;
      if (!detail?.id || !(detail.aspect && detail.aspect > 0)) return;
      const previous = aspectRef.current.get(detail.id);
      if (previous && Math.abs(previous - detail.aspect) / previous < 0.02) return;
      aspectRef.current.set(detail.id, detail.aspect);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setAspectVersion(version => version + 1), 80);
    };
    window.addEventListener('masonry-aspect', onAspect);
    return () => {
      window.removeEventListener('masonry-aspect', onAspect);
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const list = React.Children.toArray(children);
    if (list.length === 0) {
      setDistributedItems([]);
      return;
    }

    const metas = list.map((child, index) => {
      if (!React.isValidElement(child)) {
        return { id: `item-${index}`, aspect: 0.67, child };
      }
      const id = String(child.props['data-gallery-id'] || child.key || index);
      const declared = Number(child.props['data-aspect']);
      const known = aspectRef.current.get(id);
      const aspect = known || (declared > 0 ? declared : 0.67);
      if (!known && declared > 0) aspectRef.current.set(id, declared);
      return { id, aspect, child };
    });

    const galleryMetas = metas.filter(meta => meta.id.indexOf('item-') !== 0 || React.isValidElement(meta.child) && meta.child.props['data-gallery-id']);
    const packable = galleryMetas.length > 0 ? galleryMetas : metas;
    const cols = Math.max(1, Math.min(numColumns, packable.length));
    const columns: React.ReactNode[][] = Array.from({ length: cols }, () => []);
    const heights = new Array(cols).fill(0);

    packable.forEach(meta => {
      let shortest = 0;
      for (let i = 1; i < cols; i += 1) {
        if (heights[i] < heights[shortest]) shortest = i;
      }
      columns[shortest].push(meta.child);
      heights[shortest] += meta.aspect;
    });

    setDistributedItems(columns);
  }, [children, numColumns, aspectVersion, recalculationKey]);

  const columnCount = Math.max(distributedItems.length, 0);

  return (
    <div ref={containerRef} className={className} style={{ display: 'flex', gap: MASONRY_GAP, alignItems: 'flex-start' }}>
      {Array.from({ length: columnCount }).map((_, i) => (
        <div
          key={i}
          className="masonry-grid_column"
          style={{ flex: '1 1 0', minWidth: 0, alignSelf: 'flex-start' }}
        >
          {distributedItems[i]}
        </div>
      ))}
    </div>
  );
};

function readGalleryBootstrap(categorySlug: string): Gallery[] {
  if (typeof document === 'undefined') return [];
  const node = document.getElementById('page-bootstrap');
  if (!node?.textContent) return [];
  try {
    const data = JSON.parse(node.textContent);
    const galleries = Array.isArray(data?.galleries) ? data.galleries : [];
    if (!categorySlug && data?.kind === 'galleries') return galleries;
    if (categorySlug && data?.kind === 'gallery-category' && data.slug === categorySlug) return galleries;
  } catch (error) {
    return [];
  }
  return [];
}

const OriginalGalleryView: React.FC<OriginalGalleryViewProps> = ({ onGalleryHeadingChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const categoryMatch = location.pathname.match(/^\/galleries\/([^/]+)$/);
  const pathCategory = categoryMatch ? (sitePaths.normalizeFilterSlug(categoryMatch[1]) || '') : '';
  const isGalleryPage = location.pathname === '/galleries' || Boolean(pathCategory);
  const urlCategoryParam = pathCategory;
  const seededGalleries = isGalleryPage ? readGalleryBootstrap(pathCategory) : [];
  const keepServerGalleries = useRef(seededGalleries.length > 0);
  const [galleries, setGalleries] = useState<Gallery[]>(seededGalleries);
  const [galleriesReady, setGalleriesReady] = useState(seededGalleries.length > 0);
  const [showAllHomeGalleries, setShowAllHomeGalleries] = useState(false);
  const homeScrollLock = useRef<number | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(urlCategoryParam);
  const [galleryUpdateKey, setGalleryUpdateKey] = useState(0); // Force recalculation when galleries are reordered

  useLayoutEffect(() => {
    if (homeScrollLock.current == null) return;
    const y = homeScrollLock.current;
    let pinning = true;
    const restore = () => {
      if (!pinning || window.scrollY === y) return;
      window.scrollTo({ top: y, left: 0, behavior: 'auto' });
    };
    restore();
    window.addEventListener('scroll', restore, { passive: true });
    const frame = window.requestAnimationFrame(restore);
    const timers = [0, 40, 120, 280, 500].map((delay) => window.setTimeout(restore, delay));
    const release = window.setTimeout(() => {
      pinning = false;
      homeScrollLock.current = null;
      window.removeEventListener('scroll', restore);
    }, 550);
    return () => {
      pinning = false;
      window.cancelAnimationFrame(frame);
      timers.forEach((timer) => window.clearTimeout(timer));
      window.clearTimeout(release);
      window.removeEventListener('scroll', restore);
    };
  }, [showAllHomeGalleries]);

  // Keep selectedCategory in sync with URL ?category param (no caching)
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const fromQuery = sitePaths.normalizeFilterSlug(query.get('category') || query.get('filter') || '');
    if (location.pathname === '/galleries' && fromQuery) {
      navigate(sitePaths.categoryPath(fromQuery), { replace: true });
      return;
    }
    const rawCategory = categoryMatch ? categoryMatch[1].toLowerCase() : '';
    if (rawCategory && pathCategory && rawCategory !== pathCategory) {
      navigate(sitePaths.categoryPath(pathCategory), { replace: true });
      return;
    }
    setSelectedCategory(pathCategory);
  }, [location.pathname, location.search, navigate, pathCategory]);
  const [currentGallery, setCurrentGallery] = useState<Gallery | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const displaySettings = useDisplaySettings();
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const filmstripRef = React.useRef<HTMLDivElement | null>(null);
  const filmstripDragState = React.useRef({ isDown: false, startX: 0, scrollLeft: 0 });
  const preloadAnchorRef = React.useRef<{ index: number; galleryId: string } | null>(null);
  const [showFloatingInfo, setShowFloatingInfo] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareContent, setShareContent] = useState<ShareableContent | null>(null);
  const [galleryShareOpen, setGalleryShareOpen] = useState(false);
  const [galleryShareModalContent, setGalleryShareModalContent] = useState<ShareableContent | null>(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isFilmstripDragging, setIsFilmstripDragging] = useState(false);
  const [sharpReadyUrl, setSharpReadyUrl] = useState('');
  const [previewReadyUrl, setPreviewReadyUrl] = useState('');
  const openedGalleryId = currentGallery?.id || '';
  const [shownGalleryId, setShownGalleryId] = useState(openedGalleryId);
  if (openedGalleryId !== shownGalleryId) {
    setShownGalleryId(openedGalleryId);
    setPreviewReadyUrl('');
    setSharpReadyUrl('');
  }
  const [chromeHidden, setChromeHidden] = useState(false);
  const [framesOpen, setFramesOpen] = useState(false);
  const mobileViewer = useMobileViewer();
  const defaultShareImage = getConfiguredShareImage();

  const updateCategoryQuery = useCallback((categorySlug: string) => {
    if (!isGalleryPage) return;
    const nextUrl = categorySlug ? sitePaths.categoryPath(categorySlug) : '/galleries';
    if (nextUrl && nextUrl !== `${location.pathname}${location.search}`) {
      navigate(nextUrl);
    }
  }, [isGalleryPage, location.pathname, location.search, navigate]);

  const handleCategorySelect = useCallback((categorySlug: string) => {
    setSelectedCategory(categorySlug);
    updateCategoryQuery(categorySlug);
  }, [updateCategoryQuery]);

  const getSelectedCategoryInfo = useCallback(() => {
    if (!selectedCategory) return null;
    const selectedNorm = normalizeCategory(selectedCategory);
    return categories.find(cat => (
      normalizeCategory(cat.slug || '') === selectedNorm ||
      normalizeCategory(cat.name || '') === selectedNorm
    )) || null;
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (!isGalleryPage || !onGalleryHeadingChange) return;
    if (!selectedCategory) {
      onGalleryHeadingChange('All Galleries');
      return;
    }
    const saved = getSelectedCategoryInfo();
    onGalleryHeadingChange((saved?.h1 || saved?.name || '').trim());
  }, [isGalleryPage, selectedCategory, getSelectedCategoryInfo, onGalleryHeadingChange]);

  const [shareHost, setShareHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!isGalleryPage) {
      setShareHost(null);
      return;
    }
    setShareHost(document.getElementById('gallery-page-share-slot'));
  }, [isGalleryPage]);

  const getGalleryShareImageUrl = useCallback((gallery?: Gallery | null): string => {
    if (!gallery) return defaultShareImage;
    const images = gallery.images || [];
    let heroImage: any = null;
    if (gallery.heroImage && typeof gallery.heroImage === 'object') {
      heroImage = gallery.heroImage;
    } else if (gallery.heroImageIndex !== undefined && gallery.heroImageIndex >= 0 && images[gallery.heroImageIndex]) {
      heroImage = images[gallery.heroImageIndex];
    } else if (images.length > 0) {
      heroImage = images[0];
    }
    let heroImageUrl = heroImage?.url || heroImage?.secure_url || '';
    if (!heroImageUrl) return defaultShareImage;
    if (heroImageUrl.startsWith('http://')) {
      heroImageUrl = heroImageUrl.replace('http://', 'https://');
    }
    const shouldApplyHeroPositioning = Boolean(
      gallery.heroImagePosition && gallery.heroImagePosition !== 'center' ||
      (gallery.heroImageZoom && gallery.heroImageZoom !== 1.0) ||
      gallery.heroImageCropBox ||
      (gallery.heroImageCustomX !== undefined && gallery.heroImageCustomY !== undefined)
    );
    if (heroImageUrl.includes('cloudinary.com') && shouldApplyHeroPositioning) {
      return applyHeroImagePositioning(
        heroImageUrl,
        gallery.heroImagePosition || 'center',
        1200,
        630,
        gallery.heroImageZoom || 1.0,
        gallery.heroImageCustomX,
        gallery.heroImageCustomY,
        gallery.heroImageCropBox
      );
    }
    if (heroImageUrl.includes('cloudinary.com') && heroImageUrl.includes('/upload/')) {
      const [baseUrl, publicId] = heroImageUrl.split('/upload/');
      if (baseUrl && publicId) {
        return `${baseUrl}/upload/w_1200,h_630,c_fill,q_auto,f_auto/${publicId}`;
      }
    }
    return heroImageUrl;
  }, [defaultShareImage]);

  const getAdminShareImageUrl = useCallback((): string | null => {
    try {
      const shareImageData = localStorage.getItem('package_share_image');
      if (!shareImageData) return null;
      const shareImageParsed = JSON.parse(shareImageData);
      const publicImage = normalizePublicImageUrl(shareImageParsed?.imageUrl);
      if (!publicImage) return null;
      return fitShareImageUrl(publicImage);
    } catch (error) {
      console.warn('Error loading admin share image, using fallback.', error);
      return null;
    }
  }, []);


  const loadCategoriesData = async () => {
    // Prefer live categories from server; fallback to local
    try {
      const { loadLiveCategories } = await import('../../utils/CategoryAPI');
      const serverCategories = await loadLiveCategories();
      if (Array.isArray(serverCategories) && serverCategories.length > 0) {
        setCategories(serverCategories);
        
        // Force a re-render by updating state with a timestamp
        setCategories(prev => [...serverCategories, { timestamp: Date.now() } as any].slice(0, -1));
        return;
      }
    } catch (e) {
      console.warn('Falling back to local categories:', e);
    }
    try {
      const loadedCategories = loadCategories();
      setCategories(loadedCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadThemeSettings = () => {
    const themeData = localStorage.getItem('admin-theme');
    if (themeData) {
      try {
        const theme = JSON.parse(themeData);
        const root = document.documentElement;
        
        // Apply CSS custom properties to the gallery view
        if (theme.palette) {
          root.style.setProperty('--color-primary', theme.palette.primary || '#8B4513');
          root.style.setProperty('--color-secondary', theme.palette.secondary || '#A0522D');
          root.style.setProperty('--color-accent', theme.palette.accent || '#D4AF37');
          root.style.setProperty('--color-background', theme.palette.background || '#FFFFFF');
          root.style.setProperty('--color-text', theme.palette.text || '#8B4513');
          root.style.setProperty('--color-surface', theme.palette.surface || '#F8F9FA');
          root.style.setProperty('--color-border', theme.palette.border || '#E1E1E1');
          root.style.setProperty('--color-muted', theme.palette.muted || '#6B7280');
        }
      } catch (error) {
        console.error('Error loading theme settings:', error);
        // If theme is corrupted, apply high contrast default
        applyHighContrastDefault();
      }
    } else {
      // No theme saved, apply high contrast as default
      applyHighContrastDefault();
    }
  };

  const applyHighContrastDefault = () => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', '#000000');
    root.style.setProperty('--color-secondary', '#000000');
    root.style.setProperty('--color-accent', '#FFFFFF');
    root.style.setProperty('--color-background', '#FFFFFF');
    root.style.setProperty('--color-text', '#000000');
    root.style.setProperty('--color-surface', '#FFFFFF');
    root.style.setProperty('--color-border', '#000000');
    root.style.setProperty('--color-muted', '#000000');
    
    // Apply high contrast body class
    document.body.classList.add('high-contrast-bw-active');
  };

  const loadGalleries = useCallback(async () => {
    try {
      // Load galleries from server API (truly live for all visitors)
      const galleries = await loadLiveGalleries('cards');
      return galleries;
    } catch (error) {
      console.error('Error loading galleries:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    loadThemeSettings();
    loadCategoriesData();
    
    // Check for category filter from package navigation
    const categoryFilter = localStorage.getItem('gallery_category_filter');
    if (categoryFilter) {
      handleCategorySelect(categoryFilter);
      // Clear the filter so it doesn't persist on page refresh
      localStorage.removeItem('gallery_category_filter');
    }
    loadGalleries().then((loaded) => {
      const list = Array.isArray(loaded) ? loaded : [];
      if (list.length || !keepServerGalleries.current) setGalleries(list);
      setGalleriesReady(true);
    });
  }, [loadGalleries]);

  // Ensure categories are loaded when galleries change (fallback)
  useEffect(() => {
    if (galleries.length > 0 && categories.length === 0) {
      loadCategoriesData();
    }
  }, [galleries, categories.length]);

  // Force masonry layout recalculation when galleries are reordered or filtered
  // Only trigger on actual changes, not on every render
  const galleriesKeyRef = useRef<string>('');
  useEffect(() => {
    // Trigger a layout recalculation by forcing a re-render
    // This helps masonry redistribute items when order changes or category filter changes
    const currentKey = galleries.map(g => `${g.id}-${g.order}`).join(',') + selectedCategory;
    
    // Only trigger if galleries actually changed
    if (galleries.length > 0 && currentKey !== galleriesKeyRef.current) {
      galleriesKeyRef.current = currentKey;
      // Small delay to ensure DOM is updated, then trigger custom event only
      // Don't trigger resize event as it can cause scroll-related issues
      const timer = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('masonry-recalculate'));
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [galleries, selectedCategory]);

  // Force reload categories if they're not in state but exist in localStorage
  useEffect(() => {
    const adminCategories = localStorage.getItem('admin_categories');
    if (adminCategories && categories.length === 0) {
      const parsed = JSON.parse(adminCategories);
      setCategories(parsed);
    }
  }, [categories.length]);


  // Refresh categories/pills when admin updates occur
  useEffect(() => {
    const handleUpdates = () => {
      // Add delay to ensure server has processed the update
      setTimeout(() => {
        loadCategoriesData();
        loadGalleries().then((loaded) => {
          const list = Array.isArray(loaded) ? loaded : [];
          if (list.length || !keepServerGalleries.current) setGalleries(list);
        });
      }, 100);
    };
    
    const handleCategoryUpdate = () => {
      // Add delay to ensure server has processed the update
      setTimeout(() => {
        loadCategoriesData();
      }, 100);
    };
    
    const handleGalleryUpdate = () => {
      loadGalleries().then((updatedGalleries) => {
        const list = Array.isArray(updatedGalleries) ? updatedGalleries : [];
        if (!list.length && keepServerGalleries.current) return;
        setGalleries(list);
        // Force recalculation by updating the key
        setGalleryUpdateKey(prev => prev + 1);
        // Trigger a single recalculation event after a delay to allow DOM to update
        // Don't trigger multiple events as this causes white space
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('masonry-recalculate'));
        }, 300);
      });
    };
    
    window.addEventListener('categories_updated', handleCategoryUpdate);
    window.addEventListener('galleries_updated', handleGalleryUpdate);
    window.addEventListener('storage', handleUpdates);
    
    return () => {
      window.removeEventListener('categories_updated', handleCategoryUpdate);
      window.removeEventListener('galleries_updated', handleGalleryUpdate);
      window.removeEventListener('storage', handleUpdates);
    };
  }, [loadGalleries]);

  // Listen for theme updates
  useEffect(() => {
    const handleThemeUpdate = () => {
      loadThemeSettings();
    };

    const handleStorageUpdate = () => {
      loadThemeSettings();
    };

    window.addEventListener('theme_updated', handleThemeUpdate);
    window.addEventListener('storage', handleStorageUpdate);

    return () => {
      window.removeEventListener('theme_updated', handleThemeUpdate);
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, []);

  const openRequest = React.useRef(0);

  const openGallery = (gallery: Gallery) => {
    const request = ++openRequest.current;
    setCurrentGallery(gallery);
    setCurrentImageIndex(0);
    setChromeHidden(false);
    setFramesOpen(false);
    setIsModalOpen(true);
    const knownCount = gallery.imageCount ?? gallery.images?.length ?? 0;
    if (knownCount > (gallery.images?.length || 0)) {
      getGalleryById(gallery.id).then((full) => {
        if (request !== openRequest.current || !full) return;
        setCurrentGallery(full);
      }).catch((error) => {
        console.error('Error loading gallery photographs:', error);
      });
    }
  };

  const closeGallery = useCallback(() => {
    openRequest.current += 1;
    setIsModalOpen(false);
    setCurrentGallery(null);
    setCurrentImageIndex(0);
    setChromeHidden(false);
    setFramesOpen(false);
    setIsInfoOpen(false);
    setGalleryShareOpen(false);
    setGalleryShareModalContent(null);
  }, []);

  const nextImage = () => {
    if (!currentGallery || !currentGallery.images) return;
    setCurrentImageIndex((prev) => 
      prev === currentGallery.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (!currentGallery || !currentGallery.images) return;
    setCurrentImageIndex((prev) => 
      prev === 0 ? currentGallery.images.length - 1 : prev - 1
    );
  };

  const goToImage = (index: number) => {
    if (!currentGallery || !currentGallery.images) return;
    setCurrentImageIndex(index);
  };

  const stageGesture = usePhotographStage({
    onAdvance: nextImage,
    onNext: nextImage,
    onPrevious: prevImage,
    onToggleChrome: () => setChromeHidden((hidden) => !hidden),
  });

  const getCurrentImage = () => {
    if (!currentGallery || !currentGallery.images || currentGallery.images.length === 0) return null;
    return currentGallery.images[currentImageIndex];
  };

  const getPrevImage = () => {
    if (!currentGallery || !currentGallery.images || currentGallery.images.length === 0) return null;
    const prevIndex = currentImageIndex === 0 ? currentGallery.images.length - 1 : currentImageIndex - 1;
    return currentGallery.images[prevIndex];
  };

  const getNextImage = () => {
    if (!currentGallery || !currentGallery.images || currentGallery.images.length === 0) return null;
    const nextIndex = currentImageIndex === currentGallery.images.length - 1 ? 0 : currentImageIndex + 1;
    return currentGallery.images[nextIndex];
  };

  const slugify = (val: string) =>
    (val || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const normalizeCategory = (val: string) => {
    const slug = slugify(val);
    // Handle legacy plural (e.g., "portraits" vs "portrait")
    return slug.endsWith('s') ? slug.slice(0, -1) : slug;
  };

  const hasCategoryKey = (map: Map<string, number>, key: string) => {
    const norm = normalizeCategory(key);
    let found = false;
    map.forEach((_, k) => {
      if (normalizeCategory(k) === norm) {
        found = true;
      }
    });
    return found;
  };

  const getFilteredGalleries = () => {
    // Effective selected category from state or URL (normalized)
    const filterSlug = sitePaths.normalizeFilterSlug(selectedCategory || '');
    let filtered = galleries.filter(gallery => gallery.published !== false);
    if (filterSlug) {
      filtered = filtered.filter(gallery => sitePaths.galleryInFilter(gallery, filterSlug));
    } else if (selectedCategory) {
      const effectiveSelectedCategory = normalizeCategory(selectedCategory);
      filtered = filtered.filter(gallery => (
        sitePaths.galleryCategorySlugs(gallery).some((slug: string) => normalizeCategory(slug) === effectiveSelectedCategory)
      ));
    }
    
    // Sort by order property to ensure consistent ordering
    filtered = filtered.sort((a, b) => {
      const orderA = a.order || Number.MAX_SAFE_INTEGER;
      const orderB = b.order || Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
    
    // Return filtered galleries
    return filtered;
  };

  const galleryShareContent = useMemo<ShareableContent>(() => {
    const baseUrl = getPublicSiteUrl();
    const savedCategory = selectedCategory ? getSelectedCategoryInfo() : null;
    const categoryLabel = (savedCategory?.name || '').trim();
    const shareUrl = `${baseUrl}${selectedCategory ? sitePaths.categoryPath(selectedCategory) : '/galleries'}`;
    const filteredGalleries = getFilteredGalleries();
    const galleryWithImages = filteredGalleries.find(g => Array.isArray(g.images) && g.images.length > 0)
      || galleries.find(g => Array.isArray(g.images) && g.images.length > 0)
      || null;
    const adminShareImage = getAdminShareImageUrl();
    const shareImage = adminShareImage || getGalleryShareImageUrl(galleryWithImages);
    const galleryCount = filteredGalleries.length;
    const title = selectedCategory
      ? (savedCategory?.seoTitle || savedCategory?.h1 || categoryLabel || '').trim()
      : 'Photo Galleries';
    const description = selectedCategory
      ? (savedCategory?.seoDescription || savedCategory?.intro || '').trim()
      : `Take a look at ${galleryCount} photography galleries curated by Carmen Lorraine Photography.`;
    const hashtags = [
      'photography',
      'galleries',
      'carmenlorraine',
      categoryLabel ? categoryLabel.toLowerCase().replace(/\s+/g, '') : ''
    ].filter(Boolean);
    return {
      title,
      description,
      image: shareImage,
      url: shareUrl,
      hashtags,
      category: categoryLabel || 'Galleries'
    };
  }, [galleries, getAdminShareImageUrl, getGalleryShareImageUrl, getSelectedCategoryInfo, selectedCategory]);

  const openShareModal = useCallback(() => {
    setShareContent(galleryShareContent);
    setShareModalOpen(true);
  }, [galleryShareContent]);

  const getGallerySizeClass = (gallery: Gallery, index: number) => {
    // Create a balanced mix of portrait, landscape, and square
    // Pattern: wide, square, tall, wide, square, tall... with some variation
    
    // Use index to create variety while ensuring good distribution
    const pattern = [
      'gallery-wide',    // landscape
      'gallery-square',  // square
      'gallery-tall',    // portrait
      'gallery-wide',    // landscape
      'gallery-square',  // square
      'gallery-tall',    // portrait
      'gallery-wide',    // landscape
      'gallery-square',  // square
      'gallery-tall',    // portrait
    ];
    
    // Add some variation every 9 items to prevent too much repetition
    const variation = Math.floor(index / 9);
    const baseIndex = index % 9;
    
    // Occasionally swap positions for more natural variation
    if (variation % 2 === 1 && baseIndex < 3) {
      const swap = ['gallery-square', 'gallery-tall', 'gallery-wide'];
      return swap[baseIndex];
    }
    
    return pattern[baseIndex];
  };


  const renderGalleryGrid = () => {
    if (!galleriesReady) {
      return (
        <div className="galleries-loading-mark" role="status" aria-label="Loading galleries">
          <BrandMarkSpinner size="large" label="Loading galleries" caption />
        </div>
      );
    }

    const allFilteredGalleries = getFilteredGalleries();
    const savedCount = Math.min(20, Math.max(1, Number(displaySettings.galleryCount) || 11));
    const allMode = !isGalleryPage && displaySettings.homepageGalleryMode === 'all';
    const previewCount = allMode ? 10 : savedCount;
    const revealAll = isGalleryPage || (!isGalleryPage && showAllHomeGalleries);
    const displayGalleries = revealAll
      ? allFilteredGalleries
      : allFilteredGalleries.slice(0, previewCount);
    const galleryCount = displayGalleries.length;
    const shownSettings = { ...displaySettings, galleryCount: displayGalleries.length };
    const showMoreControl = !isGalleryPage && !showAllHomeGalleries && allFilteredGalleries.length > previewCount ? (
      <div className="home-show-more">
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            homeScrollLock.current = window.scrollY;
            event.currentTarget.blur();
            setShowAllHomeGalleries(true);
          }}
        >
          Show more galleries
        </button>
      </div>
    ) : null;
    const withShowMore = (grid: React.ReactNode) => (
      <>
        {grid}
        {showMoreControl}
      </>
    );

    // Check homepageGalleryStyle for homepage (not galleries page)
    // On galleries page, always use original masonry
    if (!isGalleryPage) {
      const homepageStyle = displaySettings.homepageGalleryStyle || 'masonry-original';
      
      // Render new gallery styles on homepage
      switch (homepageStyle) {
        case 'masonry-new':
          return withShowMore(<NewMasonryGallery galleries={displayGalleries} settings={shownSettings} />);
        case 'carousel':
          return withShowMore(<CarouselGallery galleries={displayGalleries} settings={shownSettings} />);
        case 'magazine':
          return withShowMore(<MagazineGallery galleries={displayGalleries} settings={shownSettings} />);
        case 'portfolio':
          return withShowMore(<PortfolioGallery galleries={displayGalleries} settings={shownSettings} />);
        case 'cinematic':
          return withShowMore(<CinematicGallery galleries={displayGalleries} settings={shownSettings} />);
        case 'single':
          const selectedGalleryId = displaySettings.selectedGallery;
          const singleGallery = selectedGalleryId 
            ? displayGalleries.find(g => g.id === selectedGalleryId) 
            : displayGalleries[0];
          return singleGallery 
            ? withShowMore(<SingleGallery gallery={singleGallery} settings={shownSettings} />)
            : <div className="no-gallery">No gallery selected</div>;
        case 'rotating':
          return withShowMore(<RotatingGallery galleries={displayGalleries} settings={shownSettings} />);
        case 'masonry-original':
        default:
          // Fall through to original masonry implementation
          break;
      }
    }

    // Original masonry implementation (for galleries page or masonry-original on homepage)
    const displayStyle = displaySettings.galleryDisplayStyle || 'medium-mixed';
    const isMasonry = displayStyle === 'masonry' || displayStyle === 'masonry-large';
    
    // Determine packing density based on gallery count
    const getPackingClass = () => {
      if (galleryCount >= 20) return 'ultra-dense';
      if (galleryCount >= 12) return 'dense-packing';
      if (galleryCount >= 6) return 'dense-packing';
      return '';
    };
    
    // Get responsive grid styles for small counts
    const getGridStyles = (): React.CSSProperties => {
      if (galleryCount === 1) {
        return {
          gridTemplateColumns: '1fr',
          maxWidth: '900px',
          gap: '2rem',
          padding: '2rem'
        };
      }
      if (galleryCount >= 2 && galleryCount <= 5) {
        return {
          gridTemplateColumns: 'repeat(2, 1fr)',
          maxWidth: '1200px',
          gap: galleryCount <= 3 ? '1.75rem' : '1.5rem',
          padding: galleryCount <= 3 ? '1.75rem' : '1.5rem'
        };
      }
      return {};
    };
    
    // Apply grid class based on galleryDisplayStyle
    const getGridClassName = () => {
      // Masonry uses different class
      if (displayStyle === 'masonry') {
        return 'mosaic-grid masonry-grid';
      }
      
      if (displayStyle === 'masonry-large') {
        return 'mosaic-grid masonry-grid masonry-large';
      }
      
      const baseClass = 'mosaic-grid';
      const countClass = `mosaic-count-${galleryCount}`;
      const packingClass = getPackingClass();
      
      // Extract size from galleryDisplayStyle (small-mixed, medium-mixed, large-mixed)
      const size = displayStyle.replace('-mixed', '');
      const mosaicSizeClass = `mosaic-${size}`;
      
      return `${baseClass} ${mosaicSizeClass} ${countClass} ${packingClass}`;
    };
    
    // Dynamic masonry breakpoint configuration based on gallery count
    // Fewer galleries = fewer columns = larger items = less white space
    const getMasonryBreakpointCols = (): { default: number; [key: number]: number } => {
      return {
        default: 3,
        1023: 2,
        639: 1
      };
    };
    
    const masonryBreakpointCols = getMasonryBreakpointCols();

    // For masonry, maintain order but optimize distribution
    // Keep the original order (respects user's gallery ordering)
    // The masonry algorithm will handle space filling
    const sortedGalleries = displayGalleries;

    // Render gallery items
    const galleryItems = displayGalleries.length === 0 ? (
          <div className="no-galleries-message">
            <div className="no-galleries-content">
              <h3>Galleries coming soon</h3>
              <p>New photography will appear here shortly.</p>
            </div>
          </div>
        ) : (
          sortedGalleries.map((gallery, index) => {
          // Always use first image as hero image (simplest approach)
          const heroImage = gallery.images && gallery.images.length > 0 
            ? gallery.images[0] 
            : null;
          
          // For masonry, don't apply size classes - let images flow naturally
          const sizeClass = isMasonry ? '' : getGallerySizeClass(gallery, index);
          
          // Apply dynamic responsive sizing for masonry based on gallery count
          // This ensures items scale appropriately to fill available space
          const getItemStyles = (): React.CSSProperties => {
            if (!isMasonry) {
              // Non-masonry grid styles
              if (galleryCount === 1) {
                return {
                  minHeight: '500px',
                  maxHeight: '600px',
                  aspectRatio: '4/3'
                };
              }
              if (galleryCount === 2) {
                return {
                  minHeight: '450px',
                  maxHeight: '550px',
                  aspectRatio: '4/3'
                };
              }
              if (galleryCount === 3) {
                return {
                  minHeight: '400px',
                  maxHeight: '500px'
                };
              }
              if (galleryCount === 4) {
                return {
                  minHeight: '380px',
                  maxHeight: '480px'
                };
              }
              if (galleryCount === 5) {
                return {
                  minHeight: '350px',
                  maxHeight: '450px'
                };
              }
              return {};
            }
            
            // Masonry height comes from the photograph's own aspect ratio.
            return {
              width: '100%'
            };
          };
          
          const heroAspect = heroImage?.width && heroImage?.height
            ? heroImage.height / heroImage.width
            : undefined;

          const heroSource = heroImage && (heroImage.url || heroImage.secure_url)
            ? applyHeroImagePositioning(
              heroImage.url || heroImage.secure_url || '',
              gallery.heroImagePosition || 'center',
              800,
              600,
              gallery.heroImageZoom || 1.0,
              gallery.heroImageCustomX,
              gallery.heroImageCustomY,
              gallery.heroImageCropBox
            )
            : '';
          const heroDelivery = heroSource ? cloudinaryDeliveryUrl(heroSource, 800) : '/placeholder.jpg';
          const heroSet = heroSource ? cloudinarySrcSet(heroSource, [480, 800, 1200]) : undefined;

          return (
            <a
              key={gallery.id}
              href={sitePaths.galleryPath(gallery)}
              className={`mosaic-item ${sizeClass}${!isGalleryPage && showAllHomeGalleries && index >= previewCount ? ' is-appended' : ''}`}
              data-gallery-id={gallery.id}
              data-aspect={heroAspect}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
                event.preventDefault();
                openGallery(gallery);
              }}
              style={getItemStyles()}
            >
              <img 
                className="mosaic-image"
                src={heroDelivery}
                srcSet={heroSet}
                sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, 33vw"
                alt={photographAlt(gallery.name, heroImage, { hero: true, description: gallery.excerpt || gallery.description })}
                width={heroImage?.width}
                height={heroImage?.height}
                loading={index === 0 ? 'eager' : 'lazy'}
                {...({ fetchpriority: index === 0 ? 'high' : 'auto' } as React.ImgHTMLAttributes<HTMLImageElement>)}
                decoding="async"
                style={{
                  objectPosition: getCSSObjectPosition(gallery.heroImagePosition || 'center')
                }}
                onLoad={(event) => {
                  const image = event.currentTarget;
                  if (image.naturalWidth > 0 && image.naturalHeight > 0) {
                    window.dispatchEvent(new CustomEvent('masonry-aspect', {
                      detail: {
                        id: gallery.id,
                        aspect: image.naturalHeight / image.naturalWidth
                      }
                    }));
                  }
                }}
              />
              <div className="mosaic-overlay">
                <div className="mosaic-info">
                  <h3 className="gallery-title">{gallery.name}</h3>
                </div>
              </div>
            </a>
          );
        })
    );

    // Use custom optimized masonry for better space filling
    if (isMasonry) {
      // Create a key that includes gallery order to force re-render when order changes
      // Include order in the key to ensure recalculation when galleries are reordered
      const galleryOrderKey = displayGalleries.map(g => `${g.id}-${g.order || 0}`).join('-');
      const orderSequence = displayGalleries.map(g => g.order || 0).join(',');
      
      // Use custom masonry component that fills shortest column first
      // Include order and category in recalculation key to force re-layout
      // Use actual gallery count (not setting) to prevent white space from non-existent galleries
      const recalculationKey = `${selectedCategory}-${galleryOrderKey}-${orderSequence}-${galleryUpdateKey}`;
      
      return withShowMore(
        <OptimizedMasonry
          breakpointCols={masonryBreakpointCols}
          className={getGridClassName()}
          key={`masonry-${selectedCategory || 'home'}-${galleryUpdateKey}`}
          recalculationKey={recalculationKey}
        >
          {galleryItems}
        </OptimizedMasonry>
      );
    }

    const gridStyles = getGridStyles();
    
    // Create a key that includes gallery order and actual gallery count to force re-render when order changes
    // Use actual gallery count (not setting) to prevent white space
    const galleryOrderKey = displayGalleries.map(g => `${g.id}-${g.order || 0}`).join('-');
    
    return withShowMore(
      <div 
        className={getGridClassName()}
        data-gallery-count={galleryCount}
        data-count={galleryCount}
        key={`grid-${selectedCategory || 'home'}-${galleryUpdateKey}`}
        style={gridStyles}
      >
        {galleryItems}
      </div>
    );
  };

  const renderSlideshow = () => {
    if (!isModalOpen || !currentGallery) return null;

    const currentImage = getCurrentImage();
    const currentRawUrl = galleryImageUrl(currentImage);
    const previewUrl = currentRawUrl ? cloudinaryDeliveryUrl(currentRawUrl, MODAL_PREVIEW_WIDTH) : '';
    const sharp = currentRawUrl ? cloudinaryLightbox(currentRawUrl) : null;
    const sharpUrl = sharp ? sharp.src : '';
    const photographPending = Boolean(
      (previewUrl || sharpUrl)
      && previewReadyUrl !== previewUrl
      && sharpReadyUrl !== sharpUrl
    );
    const gallerySwitching = Boolean(currentGallery) && previewReadyUrl === '' && sharpReadyUrl === '' && Boolean(previewUrl || sharpUrl);
    const slideAspect = currentImage?.width && currentImage?.height
      ? currentImage.width / currentImage.height
      : 1.5;
    const excerptText = (currentGallery.excerpt || '').trim();
    const fullDescriptionText = (currentGallery.description || '').trim();
    const categoryDescription = (findCategory(currentGallery.category, categories)?.description || '').trim();
    const hasInfoContent = Boolean(fullDescriptionText || categoryDescription);
    const hasMultipleImages = currentGallery.images.length > 1;
    const showFrames = !mobileViewer || framesOpen;
    const modalContent = (
      <div
        className={`gallery-modal-overlay gallery-lightbox${chromeHidden ? ' chrome-hidden' : ''}${framesOpen ? ' frames-open' : ''}`}
        onClick={closeGallery}
      >
        <div
          className="gallery-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gallery-lightbox-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="gallery-header" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-title-section" onClick={(e) => e.stopPropagation()}>
              <h3 id="gallery-lightbox-title">{currentGallery.name}</h3>
              {(excerptText || hasInfoContent) && (
                <div className="gallery-description-row">
                  {excerptText && (
                    <p className="gallery-description">{excerptText}</p>
                  )}
                  {hasInfoContent && (
                    <button
                      className="gallery-more-info-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsInfoOpen(true);
                      }}
                      type="button"
                      aria-expanded={isInfoOpen}
                      aria-controls="gallery-info-dialog"
                    >
                      <i className="fas fa-info-circle" aria-hidden="true"></i>
                      <span>More Info</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="gallery-header-actions" onClick={(e) => e.stopPropagation()}>
              <button
                className="gallery-share-quiet"
                type="button"
                aria-label="Share gallery"
                onClick={() => {
                  const content = generateShareContent('gallery', currentGallery);
                  setGalleryShareModalContent(content);
                  setGalleryShareOpen(true);
                }}
              >
                <i className="fas fa-share-alt" aria-hidden="true"></i>
                <span>Share</span>
              </button>
              <button
                className="gallery-lightbox-close"
                type="button"
                onClick={closeGallery}
                aria-label="Close gallery"
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          
          <div className="slideshow-container">
            <div className={`slideshow-wrapper${hasMultipleImages ? ' has-nav' : ''}`}>
              {hasMultipleImages && (
                <button
                  type="button"
                  className="nav-arrow-btn nav-arrow-left"
                  aria-label="Previous photograph"
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                >
                  <i className="fas fa-chevron-left" aria-hidden="true"></i>
                </button>
              )}
              {/* Main Image Area */}
              <div
                className="slideshow-main"
                onPointerDown={stageGesture.onPointerDown}
                onPointerUp={stageGesture.onPointerUp}
                onPointerCancel={stageGesture.onPointerCancel}
                onClick={stageGesture.onClick}
              >
                {currentImage && (
                  <div
                    className="slideshow-stage"
                    style={{ '--slide-aspect': String(slideAspect) } as React.CSSProperties}
                  >
                    {gallerySwitching ? (
                      <div className="brand-mark-overlay is-blocking">
                        <BrandMarkSpinner size="large" label="Loading gallery" caption />
                      </div>
                    ) : photographPending && (
                      <div className="brand-mark-overlay">
                        <BrandMarkSpinner size="small" tone="dark" label="Loading photograph" />
                      </div>
                    )}
                    {previewUrl && (
                        <img
                          src={previewUrl}
                          alt=""
                          aria-hidden="true"
                          className={`slideshow-img slideshow-img-preview${gallerySwitching ? ' is-hidden-while-switching' : ''}`}
                          decoding="sync"
                          {...({ fetchpriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>)}
                          onLoad={() => setPreviewReadyUrl(previewUrl)}
                        />
                    )}
                    {sharpUrl && (
                      <img
                        src={sharpUrl}
                        srcSet={sharp && sharp.srcSet ? sharp.srcSet : undefined}
                        sizes="100vw"
                        alt={photographAlt(currentGallery.name, currentImage, { hero: currentImageIndex === 0, description: currentGallery.excerpt || currentGallery.description })}
                        className={`slideshow-img slideshow-img-sharp${gallerySwitching ? ' is-hidden-while-switching' : ''}${sharpReadyUrl === sharpUrl ? ' is-ready' : ''}`}
                        width={currentImage.width}
                        height={currentImage.height}
                        {...({ fetchpriority: 'low' } as React.ImgHTMLAttributes<HTMLImageElement>)}
                        decoding="async"
                        onLoad={(event) => {
                          const img = event.currentTarget;
                          const finish = () => {
                            if (img.getAttribute('src') === sharpUrl) {
                              setSharpReadyUrl(sharpUrl);
                            }
                          };
                          if (typeof img.decode === 'function') {
                            img.decode().then(finish).catch(finish);
                          } else {
                            finish();
                          }
                        }}
                      />
                    )}
                  </div>
                )}
                
                {/* Mobile: Touch Indicators removed per request */}
              </div>
              {hasMultipleImages && (
                <button
                  type="button"
                  className="nav-arrow-btn nav-arrow-right"
                  aria-label="Next photograph"
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                >
                  <i className="fas fa-chevron-right" aria-hidden="true"></i>
                </button>
              )}
            </div>
            <div ref={sentinelRef} style={{height: 1, width: 1}} />
            <button
              type="button"
              className="gallery-frames-toggle"
              aria-expanded={framesOpen}
              onClick={(event) => {
                event.stopPropagation();
                setFramesOpen((open) => !open);
              }}
            >
              {framesOpen ? 'Hide photographs' : 'Photographs'}
            </button>
            <p className="gallery-lightbox-counter" aria-live="polite">
              {currentImageIndex + 1} / {currentGallery.images.length}
            </p>
            
            {/* Bottom Filmstrip. Mobile keeps these unloaded until asked for. */}
            {showFrames && <div
              className={`gallery-filmstrip ${isFilmstripDragging ? 'dragging' : ''}`}
              role="list"
              ref={filmstripRef}
              onWheel={(event) => {
                if (event.deltaY === 0) return;
                event.preventDefault();
                const target = event.currentTarget;
                target.scrollLeft += event.deltaY;
              }}
              onPointerDown={(event) => {
                const target = event.target as HTMLElement;
                if (target.closest('.gallery-filmstrip-item')) {
                  return;
                }
                if (!filmstripRef.current) return;
                filmstripRef.current.setPointerCapture(event.pointerId);
                filmstripDragState.current = {
                  isDown: true,
                  startX: event.clientX,
                  scrollLeft: filmstripRef.current.scrollLeft
                };
                setIsFilmstripDragging(true);
              }}
              onPointerMove={(event) => {
                if (!filmstripRef.current || !filmstripDragState.current.isDown) return;
                const dx = event.clientX - filmstripDragState.current.startX;
                filmstripRef.current.scrollLeft = filmstripDragState.current.scrollLeft - dx;
              }}
              onPointerUp={(event) => {
                if (!filmstripRef.current) return;
                filmstripDragState.current.isDown = false;
                setIsFilmstripDragging(false);
                filmstripRef.current.releasePointerCapture(event.pointerId);
              }}
              onPointerLeave={(event) => {
                if (!filmstripRef.current) return;
                filmstripDragState.current.isDown = false;
                setIsFilmstripDragging(false);
                filmstripRef.current.releasePointerCapture(event.pointerId);
              }}
            >
              {currentGallery.images.map((image, index) => (
                <button
                  key={index}
                  className={`gallery-filmstrip-item ${index === currentImageIndex ? 'active' : ''}`}
                  onPointerEnter={() => preloadImage(cloudinaryDeliveryUrl(galleryImageUrl(image), cloudinaryLightboxWidth(window.innerWidth, window.devicePixelRatio)))}
                  onPointerDown={() => preloadImage(cloudinaryDeliveryUrl(galleryImageUrl(image), cloudinaryLightboxWidth(window.innerWidth, window.devicePixelRatio)))}
                  onClick={() => setCurrentImageIndex(index)}
                  type="button"
                  aria-label={`Go to image ${index + 1}`}
                  aria-current={index === currentImageIndex ? 'true' : undefined}
                  role="listitem"
                >
                  <img
                    src={cloudinaryDeliveryUrl(image.url || image.secure_url || '', 240)}
                    alt={photographAlt(currentGallery.name, image)}
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>}
          </div>
        </div>
        {showFloatingInfo && (
          <div className="floating-info-bar">
            <div className="floating-info-content">
              <div className="floating-info-title">{currentGallery.name}</div>
              <div className="floating-info-meta">
                <span>{currentImageIndex + 1} / {currentGallery.images.length}</span>
              </div>
            </div>
          </div>
        )}
        {isInfoOpen && (
          <GalleryInfoDialog
            name={currentGallery.name}
            category={currentGallery.category}
            excerpt={excerptText}
            description={fullDescriptionText}
            categories={categories}
            onClose={() => setIsInfoOpen(false)}
          />
        )}
        {galleryShareModalContent && (
          <ShareModal
            isOpen={galleryShareOpen}
            onClose={() => {
              setGalleryShareOpen(false);
              setGalleryShareModalContent(null);
            }}
            content={galleryShareModalContent}
            onShare={() => {}}
          />
        )}
      </div>
    );

    // Render modal in a portal to ensure it appears above all other elements
    return createPortal(modalContent, document.body);
  };

  useEffect(() => {
    if (!isModalOpen || !currentGallery) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        if (isInfoOpen) {
          setIsInfoOpen(false);
        } else if (galleryShareOpen) {
          setGalleryShareOpen(false);
          setGalleryShareModalContent(null);
        } else {
          closeGallery();
        }
        return;
      }

      if (isInfoOpen || galleryShareOpen) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!currentGallery.images || currentGallery.images.length < 2) return;

      event.preventDefault();
      const lastIndex = currentGallery.images.length - 1;
      setCurrentImageIndex((prev) => {
        if (event.key === 'ArrowLeft') return prev === 0 ? lastIndex : prev - 1;
        return prev === lastIndex ? 0 : prev + 1;
      });
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen, isInfoOpen, galleryShareOpen, currentGallery, closeGallery]);

  // Observe when bottom sentinel is in view; if not in view, show floating info
  useEffect(() => {
    if (!isModalOpen) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setShowFloatingInfo(!entry.isIntersecting);
      },
      { root: null, threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isModalOpen, currentGallery, currentImageIndex]);

  // Warm the current photograph and one neighbour on each side at the
  // srcset width this viewport will display. After a step forward or back,
  // also warm one further frame in that direction. A closed mobile filmstrip
  // is not mounted, so it does not add further requests.
  useEffect(() => {
    if (!isModalOpen || !currentGallery?.images?.length) {
      preloadAnchorRef.current = null;
      return;
    }
    const images = currentGallery.images;
    const count = images.length;
    const galleryId = String(currentGallery.id || currentGallery.name || '');
    const anchor = preloadAnchorRef.current;
    preloadAnchorRef.current = { index: currentImageIndex, galleryId };
    const offsets = [0, 1, -1];
    if (anchor && anchor.galleryId === galleryId && anchor.index !== currentImageIndex) {
      const stepsForward = (currentImageIndex - anchor.index + count) % count;
      offsets.push(stepsForward <= count / 2 ? 2 : -2);
    }
    const width = cloudinaryLightboxWidth(window.innerWidth, window.devicePixelRatio);
    const seen = new Set<string>();
    offsets.forEach((offset) => {
      const image = images[(currentImageIndex + offset + count) % count];
      const rawUrl = galleryImageUrl(image);
      const preloadUrl = cloudinaryDeliveryUrl(rawUrl, width);
      if (!preloadUrl || seen.has(preloadUrl)) return;
      seen.add(preloadUrl);
      const loader = new Image();
      loader.decoding = 'async';
      loader.src = preloadUrl;
      const displayedSrc = cloudinaryLightbox(rawUrl).src;
      if (offset === 0 && preloadUrl === displayedSrc && loader.complete && loader.naturalWidth > 0) {
        setSharpReadyUrl(displayedSrc);
      }
    });
  }, [isModalOpen, currentGallery, currentImageIndex]);

  // Keep filmstrip aligned with the current image without scrolling the page.
  useEffect(() => {
    if (!isModalOpen || !filmstripRef.current) return;
    const strip = filmstripRef.current;
    const frame = window.requestAnimationFrame(() => {
      const activeItem = strip.querySelector(
        '.gallery-filmstrip-item.active'
      ) as HTMLElement | null;
      if (!activeItem) return;
      const target = activeItem.offsetLeft - (strip.clientWidth - activeItem.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: 'auto' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isModalOpen, currentImageIndex, framesOpen, mobileViewer]);

  const renderShareButton = () => {
    if (!isGalleryPage) return null;

    const button = (
      <button className="share-button share-button-trigger" type="button" onClick={openShareModal}>
        <i className="fas fa-share-alt"></i>
        <span>Share</span>
      </button>
    );

    if (shareHost) {
      return createPortal(button, shareHost);
    }

    return null;
  };

  return (
    <div className="original-gallery-view">
      {renderShareButton()}
      {renderGalleryGrid()}
      {renderSlideshow()}
      {shareContent && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setShareContent(null);
          }}
          content={shareContent}
          onShare={() => {}}
        />
      )}
    </div>
  );
};

export default OriginalGalleryView;
